/**
 * Tile-based loading for map data
 * Splits bounding box into smaller tiles and loads them sequentially
 * for a more continuous loading experience
 */

export interface Tile {
    bbox: string; // "minLon,minLat,maxLon,maxLat"
    minLon: number;
    minLat: number;
    maxLon: number;
    maxLat: number;
}

/**
 * Splits a bounding box into smaller tiles
 * @param bbox - Bounding box string "minLon,minLat,maxLon,maxLat"
 * @param tileCount - Number of tiles per dimension (default 3 = 3x3 = 9 tiles)
 * @returns Array of tile bounding boxes
 */
export function splitBoundingBoxIntoTiles(
    bbox: string,
    tileCount: number = 3
): Tile[] {
    const parts = bbox.split(",").map((p) => parseFloat(p.trim()));
    if (parts.length !== 4 || parts.some((p) => isNaN(p))) {
        return [];
    }

    const [minLon, minLat, maxLon, maxLat] = parts;
    const lonStep = (maxLon - minLon) / tileCount;
    const latStep = (maxLat - minLat) / tileCount;

    const tiles: Tile[] = [];

    for (let i = 0; i < tileCount; i++) {
        for (let j = 0; j < tileCount; j++) {
            const tileMinLon = minLon + i * lonStep;
            const tileMaxLon = minLon + (i + 1) * lonStep;
            const tileMinLat = minLat + j * latStep;
            const tileMaxLat = minLat + (j + 1) * latStep;

            tiles.push({
                bbox: `${tileMinLon},${tileMinLat},${tileMaxLon},${tileMaxLat}`,
                minLon: tileMinLon,
                minLat: tileMinLat,
                maxLon: tileMaxLon,
                maxLat: tileMaxLat
            });
        }
    }

    return tiles;
}

/**
 * Determines if tile-based loading should be used
 * Based on bounding box size (larger boxes = use tiles)
 *
 * DISABLED: Tiling creates too many concurrent requests to remote database
 * When enabled, it creates 9-16 parallel requests which overwhelm the database
 * connection pool and network bandwidth to the remote DB (192.168.1.4)
 */
export function shouldUseTileLoading(_bbox: string): boolean {
    // Temporarily disabled - too many concurrent requests to remote DB
    return false;

    // Original logic (disabled):
    // const parts = bbox.split(",").map((p) => parseFloat(p.trim()));
    // if (parts.length !== 4) return false;
    // const [minLon, minLat, maxLon, maxLat] = parts;
    // const width = maxLon - minLon;
    // const height = maxLat - minLat;
    // return width > 0.01 || height > 0.01;
}

/**
 * Calculates optimal tile count based on bounding box size
 */
export function calculateTileCount(bbox: string): number {
    const parts = bbox.split(",").map((p) => parseFloat(p.trim()));
    if (parts.length !== 4) return 2;

    const [minLon, minLat, maxLon, maxLat] = parts;
    const width = maxLon - minLon;
    const height = maxLat - minLat;
    const maxDimension = Math.max(width, height);

    // Scale tile count based on bounding box size
    if (maxDimension > 0.2) return 4; // Very zoomed out: 4x4 = 16 tiles
    if (maxDimension > 0.1) return 3; // Zoomed out: 3x3 = 9 tiles
    if (maxDimension > 0.03) return 2; // Medium: 2x2 = 4 tiles
    return 2; // Even when zoomed in, use 2x2 tiles for smoother loading
}